import { useMemo, useState } from 'react';
import { X, ChevronUp, ChevronDown, Trash2, AlertCircle } from 'lucide-react';
import { useCollaboratorsForMessaging } from '../../hooks/queries/useMessaging';
import { useCreateCircuit } from '../../hooks/queries/useSignatureCircuits';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { DatePicker } from '../ui/DatePicker';
import { CollaboratorSelect } from '../collaborators/CollaboratorSelect';
import { PARAPHEUR_COPY } from '../../services/signature/parapheurCopy';
import { canSubmitVisaRequest } from '../../services/signature/circuitRevision';

interface Props {
  fileId: string;
  fileName: string;
  kind?: 'visa' | 'validation';
  onClose: () => void;
  onCreated?: (circuitId: string) => void;
}

interface SignerRow {
  collaborator_id: string;
  deadline_at: string; // yyyy-mm-dd or ''
}

export function CreateCircuitModal({ fileId, fileName, kind = 'visa', onClose, onCreated }: Props) {
  const isValidation = kind === 'validation';
  const { data: collaborators = [] } = useCollaboratorsForMessaging();
  const [title, setTitle] = useState(
    isValidation ? `Validation : ${fileName}` : `Visa : ${fileName}`,
  );
  const [rows, setRows] = useState<SignerRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateCircuit();

  const usedIds = useMemo(() => new Set(rows.map((r) => r.collaborator_id)), [rows]);
  const available = collaborators.filter((c) => !usedIds.has(c.id));

  function addSigner(id: string) {
    setRows((prev) => [...prev, { collaborator_id: id, deadline_at: '' }]);
  }
  function removeSigner(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveUp(idx: number) {
    if (idx === 0) return;
    setRows((prev) => {
      const copy = prev.slice();
      const tmp = copy[idx - 1]!;
      copy[idx - 1] = copy[idx]!;
      copy[idx] = tmp;
      return copy;
    });
  }
  function moveDown(idx: number) {
    setRows((prev) => {
      if (idx === prev.length - 1) return prev;
      const copy = prev.slice();
      const tmp = copy[idx + 1]!;
      copy[idx + 1] = copy[idx]!;
      copy[idx] = tmp;
      return copy;
    });
  }
  function setDeadline(idx: number, value: string) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, deadline_at: value } : r)));
  }

  function handleSubmit() {
    setError(null);
    if (!title.trim()) {
      setError('Titre obligatoire');
      return;
    }
    if (rows.length === 0) {
      setError('Au moins un signataire requis');
      return;
    }
    if (!canSubmitVisaRequest({ title: title.trim(), signers: rows })) {
      setError('Échéance obligatoire pour chaque signataire');
      return;
    }
    create.mutate(
      {
        file_id: fileId,
        title: title.trim(),
        kind,
        signers: rows.map((r) => ({
          collaborator_id: r.collaborator_id,
          deadline_at: r.deadline_at ? new Date(r.deadline_at + 'T17:00:00').toISOString() : null,
        })),
      },
      {
        onSuccess: (circuitId) => {
          onCreated?.(circuitId);
          onClose();
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Erreur'),
      },
    );
  }

  function collabOf(id: string) {
    return collaborators.find((c) => c.id === id);
  }
  function nameOf(id: string): string {
    return collabOf(id)?.full_name ?? '—';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" aria-hidden />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div>
            <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
              {isValidation ? 'Demander une validation' : 'Demander un visa'}
            </h2>
            <p className="text-xs text-cime-text-muted mt-0.5">
              {isValidation
                ? 'Choisissez les signataires. Ils seront prévenus tout de suite, sans case ni tampon.'
                : 'Choisissez les signataires. Vous poserez les cases, puis vous enverrez la demande.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <Input
            label="Titre du circuit"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex : Validation contrat client"
          />

          <div>
            <p className="text-sm font-medium text-neutral-700 mb-1">
              Signataires dans l'ordre (séquentiel)
            </p>
            {rows.length === 0 && (
              <p className="text-xs italic text-cime-text-muted py-2">Ajoutez au moins un signataire ci-dessous.</p>
            )}
            <ol className="space-y-3">
              {rows.map((r, idx) => (
                <li key={`${r.collaborator_id}-${idx}`} className="border border-neutral-200 rounded-cime px-3 py-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs tabular-nums text-neutral-500 w-5">{idx + 1}.</span>
                    <span className="flex-1 min-w-0 flex items-center gap-2">
                      <Avatar
                        name={nameOf(r.collaborator_id)}
                        src={collabOf(r.collaborator_id)?.avatar_url}
                        size="xs"
                      />
                      <span className="text-sm truncate">{nameOf(r.collaborator_id)}</span>
                    </span>
                    <button type="button" onClick={() => moveUp(idx)} disabled={idx === 0} className="p-1 text-neutral-400 hover:text-neutral-700 disabled:opacity-30">
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveDown(idx)} disabled={idx === rows.length - 1} className="p-1 text-neutral-400 hover:text-neutral-700 disabled:opacity-30">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => removeSigner(idx)} className="p-1 text-red-500 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <DatePicker
                    label="Échéance"
                    required
                    value={r.deadline_at}
                    onChange={(iso) => setDeadline(idx, iso)}
                    aria-label={`Échéance ${nameOf(r.collaborator_id)}`}
                  />
                </li>
              ))}
            </ol>
          </div>

          {available.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{PARAPHEUR_COPY.addSigner}</label>
              <CollaboratorSelect
                options={available}
                value=""
                onChange={(id) => { if (id) addSigner(id); }}
                placeholder="— Choisir —"
              />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            isLoading={create.isPending}
            disabled={create.isPending || !canSubmitVisaRequest({ title, signers: rows })}
            onClick={handleSubmit}
          >
            {isValidation ? 'Demander une validation' : 'Demander un visa'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
